import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setPreferences,
  nextStep,
  prevStep,
  selectPreferences,
  selectClinicInfo,
} from "../../../store/slices/registrationSlice";
import {
  SlidersHorizontal,
  ArrowLeft,
  ArrowRight,
  Plus,
  X,
  UsersThree,
  UserPlus,
  VideoCamera,
  Clock,
} from "@phosphor-icons/react";
import CustomClinikitButton from "../../ui/CustomClinikitButton";
import HourSelect from "../../ui/HourSelect";
import {
  CALENDAR_START_HOUR_OPTIONS,
  normalizeCalendarStartHour,
} from "../../../utils/calendarHours";
import {
  MEETING_DURATIONS,
  getSuggestedTreatmentTypes,
  isTempCustomersDefaultOn,
} from "../../../data/clinicTypesConfig";
import { CATEGORY_COLORS } from "../../../utils/treatmentTypeRules";
import { scrollToFirstError } from "../../../utils/formScroll";
import "./Steps.css";
import "./PreferencesStep.css";

const PRESET_DURATIONS = [15, 30, 45, 60];
const MIN_DURATION = 1;

let rowKeyCounter = 0;
const nextRowKey = () => `tt-${Date.now()}-${rowKeyCounter++}`;

// General type takes CATEGORY_COLORS[0]; rows start at index 1.
const colorForIndex = (index) => CATEGORY_COLORS[(index + 1) % CATEGORY_COLORS.length];

const buildSuggestedRows = (clinicType) =>
  getSuggestedTreatmentTypes(clinicType).map((preset, index) => ({
    key: nextRowKey(),
    name: preset.name,
    duration: preset.duration,
    rate: "",
    color: colorForIndex(index),
    isSuggested: true,
  }));

/**
 * Seed / re-seed suggested rows when the step is first visited or when the
 * clinic type changed since the suggestions were generated. User-added rows
 * are always preserved.
 */
const resolveInitialRows = (saved, clinicType) => {
  if (saved.treatmentTypes === null || saved.treatmentTypesSeededFor !== clinicType) {
    const userRows = (saved.treatmentTypes || []).filter((r) => !r.isSuggested);
    return [...buildSuggestedRows(clinicType), ...userRows];
  }
  return saved.treatmentTypes;
};

const TOGGLES = [
  {
    key: "clinicManagesCouplesAsPatients",
    icon: UsersThree,
    label: "זוגות כמטופלים",
    description: "יצירת מטופל מסוג זוג עם פרטי שני בני הזוג",
    // Couple intake is not a thing for a nutritionist — hidden at signup,
    // in clinic settings and in create-patient (selectClinicManagesCouplesAsPatients).
    hiddenForClinicTypes: ["nutrition"],
  },
  {
    key: "tempCustomersEnabled",
    icon: UserPlus,
    label: "לקוחות מזדמנים",
    description: "קביעת פגישה למי שאינו מטופל קבוע, בלי לפתוח תיק",
  },
  {
    key: "onlineMeetingsEnabled",
    icon: VideoCamera,
    label: "פגישות וידאו אונליין",
    description: "פגישות וידאו מתוך המערכת (עד 5 שעות בחודש)",
  },
];

function PreferencesStep() {
  const dispatch = useDispatch();
  const saved = useSelector(selectPreferences);
  const clinicInfo = useSelector(selectClinicInfo);
  const clinicType = clinicInfo.clinicType;
  const visibleToggles = TOGGLES.filter(
    (t) => !t.hiddenForClinicTypes?.includes(clinicType)
  );

  const isCustomDuration =
    saved.defaultMeetingTime && !PRESET_DURATIONS.includes(saved.defaultMeetingTime);

  const [general, setGeneral] = useState({
    name: saved.generalTreatmentName || "טיפול כללי",
    rate: saved.defaultHourlyRate || "",
    duration: saved.defaultMeetingTime || 45,
  });
  const [isCustomDurationSelected, setIsCustomDurationSelected] =
    useState(!!isCustomDuration);
  const [customDurationValue, setCustomDurationValue] = useState(
    isCustomDuration ? String(saved.defaultMeetingTime) : ""
  );

  const [rows, setRows] = useState(() => resolveInitialRows(saved, clinicType));
  // First time this step is shown for the current clinic type (same signal
  // resolveInitialRows uses). Walk-in-heavy types (e.g. aesthetics) get the
  // "לקוחות מזדמנים" toggle pre-checked; a user's explicit choice is kept
  // on revisit.
  const isFirstVisitForType =
    saved.treatmentTypes === null || saved.treatmentTypesSeededFor !== clinicType;
  const [toggles, setToggles] = useState({
    clinicManagesCouplesAsPatients: saved.clinicManagesCouplesAsPatients === true,
    tempCustomersEnabled:
      saved.tempCustomersEnabled === true ||
      (isFirstVisitForType && isTempCustomersDefaultOn(clinicType)),
    onlineMeetingsEnabled: saved.onlineMeetingsEnabled === true,
  });

  const [calendarStartHour, setCalendarStartHour] = useState(() =>
    normalizeCalendarStartHour(saved.calendarStartHour)
  );

  const [errors, setErrors] = useState({ general: {}, rows: {} });
  const formRef = useRef(null);

  // ---------- General type ----------
  const handleGeneralChange = (field, value) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
    if (errors.general[field]) {
      setErrors((prev) => ({ ...prev, general: { ...prev.general, [field]: "" } }));
    }
  };

  const handleDurationSelect = (value) => {
    if (value === "custom") {
      setIsCustomDurationSelected(true);
      if (customDurationValue) {
        handleGeneralChange("duration", parseInt(customDurationValue, 10));
      }
    } else {
      setIsCustomDurationSelected(false);
      handleGeneralChange("duration", value);
    }
  };

  const handleCustomDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomDurationValue(value);
    if (value) handleGeneralChange("duration", parseInt(value, 10));
  };

  // ---------- Rows ----------
  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
    if (errors.rows[key]?.[field]) {
      setErrors((prev) => ({
        ...prev,
        rows: { ...prev.rows, [key]: { ...prev.rows[key], [field]: "" } },
      }));
    }
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
    setErrors((prev) => {
      const nextRows = { ...prev.rows };
      delete nextRows[key];
      return { ...prev, rows: nextRows };
    });
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: nextRowKey(),
        name: "",
        duration: "",
        rate: "",
        color: colorForIndex(prev.length),
        isSuggested: false,
      },
    ]);
  };

  const [openColorKey, setOpenColorKey] = useState(null);
  const colorPopoverRef = useRef(null);

  const setRowColor = (key, color) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, color } : r)));
    setOpenColorKey(null);
  };

  useEffect(() => {
    if (!openColorKey) return undefined;
    const onPointerDown = (e) => {
      if (colorPopoverRef.current && !colorPopoverRef.current.contains(e.target)) {
        setOpenColorKey(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenColorKey(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openColorKey]);

  // ---------- Validation ----------
  const validate = () => {
    const generalErrors = {};
    const rowErrors = {};

    const generalName = general.name.trim();
    if (generalName.length < 2) generalErrors.name = "מינימום 2 תווים";

    const generalDuration = Number(general.duration);
    if (!generalDuration || generalDuration < MIN_DURATION) {
      generalErrors.duration = `משך מינימלי ${MIN_DURATION} דק׳`;
    }

    if (general.rate === "" || general.rate == null) {
      generalErrors.rate = "יש להזין תעריף (ניתן להזין 0)";
    } else if (Number.isNaN(Number(general.rate)) || Number(general.rate) < 0) {
      generalErrors.rate = "תעריף לא תקין";
    }

    const seenNames = new Set([generalName.toLowerCase()]);
    rows.forEach((row) => {
      const e = {};
      const name = row.name.trim();
      if (name.length < 2) {
        e.name = "מינימום 2 תווים";
      } else if (seenNames.has(name.toLowerCase())) {
        e.name = "סוג טיפול זה כבר קיים";
      } else {
        seenNames.add(name.toLowerCase());
      }

      if (row.duration !== "" && row.duration != null) {
        const d = Number(row.duration);
        if (!d || d < MIN_DURATION) e.duration = `מינימום ${MIN_DURATION}`;
      }
      if (row.rate !== "" && row.rate != null) {
        const r = Number(row.rate);
        if (Number.isNaN(r) || r < 0) e.rate = "לא תקין";
      }
      if (Object.keys(e).length) rowErrors[row.key] = e;
    });

    setErrors({ general: generalErrors, rows: rowErrors });
    return Object.keys(generalErrors).length === 0 && Object.keys(rowErrors).length === 0;
  };

  // ---------- Navigation ----------
  const buildPayload = () => ({
    generalTreatmentName: general.name.trim() || "טיפול כללי",
    defaultHourlyRate: general.rate,
    defaultMeetingTime: Number(general.duration) || 45,
    treatmentTypes: rows,
    treatmentTypesSeededFor: clinicType,
    calendarStartHour: normalizeCalendarStartHour(calendarStartHour),
    ...toggles,
    // A hidden toggle is never persisted as true (e.g. the user picked
    // "nutrition" after flipping it on under another clinic type).
    ...Object.fromEntries(
      TOGGLES.filter((t) => t.hiddenForClinicTypes?.includes(clinicType)).map(
        (t) => [t.key, false]
      )
    ),
  });

  const handleBack = () => {
    dispatch(setPreferences(buildPayload()));
    dispatch(prevStep());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      dispatch(setPreferences(buildPayload()));
      dispatch(nextStep());
    } else {
      scrollToFirstError(formRef.current);
    }
  };

  const durationPlaceholder = String(Number(general.duration) || 45);
  const ratePlaceholder = general.rate !== "" ? String(general.rate) : "—";

  return (
    <div className="step-container">
      <div className="step-header">
        <SlidersHorizontal size={24} weight="duotone" className="step-icon" />
        <h2>העדפות עבודה</h2>
        <p>
          כמה הגדרות שיחסכו זמן בקביעת פגישות. הכל לא חובה וניתן לשינוי בכל עת
          בהגדרות הקליניקה.
        </p>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="step-form">
        {/* ---------- A. General treatment type ---------- */}
        <div className="form-group">
          <label>טיפול כללי (ברירת מחדל)</label>
          <p className="field-description">
            המשך והתעריף כאן ישמשו כברירת מחדל לכל פגישה.
          </p>
          <div className="regpref-general">
            <div className="regpref-general-row">
              <span
                className="regpref-color-dot"
                style={{ background: CATEGORY_COLORS[0] }}
                aria-hidden="true"
              />
              <input
                type="text"
                value={general.name}
                onChange={(e) => handleGeneralChange("name", e.target.value)}
                placeholder="טיפול"
                maxLength={40}
                className={errors.general.name ? "error" : ""}
                aria-label="שם סוג הטיפול הכללי"
              />
            </div>
            {errors.general.name && (
              <span className="field-error">{errors.general.name}</span>
            )}

            <div className="regpref-general-field">
              <span className="regpref-sublabel">משך פגישה</span>
              <div className="duration-options">
                {MEETING_DURATIONS.map((duration) =>
                  duration.value === "custom" ? (
                    <div
                      key={duration.value}
                      className={`duration-btn duration-custom-btn ${
                        isCustomDurationSelected ? "selected" : ""
                      }`}
                      onClick={() => handleDurationSelect("custom")}
                    >
                      {isCustomDurationSelected ? (
                        <div className="custom-duration-input-wrapper">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={customDurationValue}
                            onChange={handleCustomDurationChange}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="דק׳"
                            className="custom-duration-input"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <span>{duration.label}</span>
                      )}
                    </div>
                  ) : (
                    <button
                      key={duration.value}
                      type="button"
                      className={`duration-btn ${
                        !isCustomDurationSelected && general.duration === duration.value
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleDurationSelect(duration.value)}
                    >
                      {duration.label}
                    </button>
                  )
                )}
              </div>
              {errors.general.duration && (
                <span className="field-error">{errors.general.duration}</span>
              )}
            </div>

            <div className="regpref-general-field">
              <span className="regpref-sublabel regpref-sublabel-required">תעריף</span>
              <div className="input-with-icon regpref-rate-input">
                <input
                  type="number"
                  className={`input-field-cust-padding ${errors.general.rate ? "error" : ""}`}
                  value={general.rate}
                  onChange={(e) => handleGeneralChange("rate", e.target.value)}
                  placeholder="350"
                  min="0"
                  required
                  aria-required="true"
                  dir="ltr"
                  aria-label="תעריף ברירת מחדל"
                />
                <span className="input-suffix">₪</span>
              </div>
              {errors.general.rate && (
                <span className="field-error">{errors.general.rate}</span>
              )}
              <p className="field-hint">ניתן לשנות לכל מטופל ולכל סוג טיפול בנפרד</p>
            </div>
          </div>
        </div>

        {/* ---------- B. Additional treatment types ---------- */}
        <div className="form-group">
          <label>
            סוגי טיפול נוספים
            <span className="optional-tag">לא חובה</span>
          </label>
          <p className="field-description">
            לדוגמה: אבחון, הדרכת הורים. אפשר לדלג ולהוסיף אחר כך בהגדרות ← התאמה
            אישית.
          </p>

          {rows.length > 0 && (
            <div className="regpref-rows">
              {rows.map((row) => {
                const rowErr = errors.rows[row.key] || {};
                return (
                  <div key={row.key} className="regpref-row">
                    <div className="regpref-row-main">
                      <div
                        className="regpref-color-wrap"
                        ref={openColorKey === row.key ? colorPopoverRef : null}
                      >
                        <button
                          type="button"
                          className={`regpref-color-btn ${
                            openColorKey === row.key ? "open" : ""
                          }`}
                          style={{ background: row.color }}
                          onClick={() =>
                            setOpenColorKey((k) => (k === row.key ? null : row.key))
                          }
                          title="החלפת צבע"
                          aria-label="החלפת צבע"
                          aria-haspopup="true"
                          aria-expanded={openColorKey === row.key}
                        />
                        {openColorKey === row.key && (
                          <div
                            className="regpref-color-popover"
                            role="radiogroup"
                            aria-label="בחירת צבע"
                          >
                            {CATEGORY_COLORS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                role="radio"
                                aria-checked={row.color === color}
                                className={`regpref-swatch ${
                                  row.color === color ? "selected" : ""
                                }`}
                                style={{ background: color }}
                                onClick={() => setRowColor(row.key, color)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <input
                        type="text"
                        className={`regpref-name-input ${rowErr.name ? "error" : ""}`}
                        value={row.name}
                        onChange={(e) => updateRow(row.key, "name", e.target.value)}
                        placeholder="שם סוג הטיפול"
                        maxLength={40}
                        aria-label="שם סוג הטיפול"
                      />
                      {row.isSuggested && (
                        <span className="regpref-suggested-badge">מוצע</span>
                      )}
                      <button
                        type="button"
                        className="regpref-remove-btn"
                        onClick={() => removeRow(row.key)}
                        aria-label="הסרה"
                        title="הסרה"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>

                    <div className="regpref-row-meta">
                      <div className="regpref-meta-field">
                        <input
                          type="number"
                          inputMode="numeric"
                          className={rowErr.duration ? "error" : ""}
                          value={row.duration}
                          onChange={(e) => updateRow(row.key, "duration", e.target.value)}
                          placeholder={durationPlaceholder}
                          min={MIN_DURATION}
                          dir="ltr"
                          aria-label="משך בדקות"
                        />
                        <span className="regpref-meta-suffix">דק׳</span>
                      </div>
                      <div className="regpref-meta-field">
                        <input
                          type="number"
                          inputMode="decimal"
                          className={rowErr.rate ? "error" : ""}
                          value={row.rate}
                          onChange={(e) => updateRow(row.key, "rate", e.target.value)}
                          placeholder={ratePlaceholder}
                          min="0"
                          dir="ltr"
                          aria-label="תעריף"
                        />
                        <span className="regpref-meta-suffix">₪</span>
                      </div>
                    </div>

                    {(rowErr.name || rowErr.duration || rowErr.rate) && (
                      <span className="field-error">
                        {rowErr.name || rowErr.duration || rowErr.rate}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button type="button" className="regpref-add-btn" onClick={addRow}>
            <Plus size={14} weight="bold" />
            <span>הוסף סוג טיפול</span>
          </button>
          <p className="field-hint">
            משך ותעריף ריקים יירשו את ברירת המחדל של הטיפול הכללי.
          </p>
        </div>

        {/* ---------- C. Calendar start hour ---------- */}
        <div className="form-group">
          <label htmlFor="regpref-calendar-start">שעת פתיחת היומן</label>
          <p className="field-description">
            השעה הראשונה שמוצגת ביומן — בדרך כלל שעת הפגישה הראשונה ביום.
            היומן יוצג משעה זו ועד 23:00.
          </p>
          <div className="regpref-calendar-start">
            <span className="regpref-calendar-start-icon" aria-hidden="true">
              <Clock size={18} weight="duotone" />
            </span>
            <div className="regpref-calendar-start-select">
              <HourSelect
                id="regpref-calendar-start"
                value={calendarStartHour}
                options={CALENDAR_START_HOUR_OPTIONS}
                onChange={setCalendarStartHour}
                ariaLabel="שעת פתיחת היומן"
              />
            </div>
          </div>
          <p className="field-hint">ניתן לשנות בקלות בכל עת בהגדרות הקליניקה ← התאמה אישית.</p>
        </div>

        {/* ---------- D. Feature toggles ---------- */}
        <div className="form-group">
          <label>אפשרויות נוספות</label>
          <div className="regpref-toggles">
            {visibleToggles.map((t) => {
              const Icon = t.icon;
              const checked = toggles[t.key] === true;
              return (
                <label
                  key={t.key}
                  className={`regpref-toggle-row ${checked ? "active" : ""}`}
                >
                  <span className="regpref-toggle-icon">
                    <Icon size={18} weight={checked ? "fill" : "duotone"} />
                  </span>
                  <span className="regpref-toggle-text">
                    <span className="regpref-toggle-label">{t.label}</span>
                    <span className="regpref-toggle-desc">{t.description}</span>
                  </span>
                  <span className="regpref-switch">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setToggles((prev) => ({ ...prev, [t.key]: e.target.checked }))
                      }
                    />
                    <span className="regpref-switch-slider" />
                  </span>
                </label>
              );
            })}
          </div>
          <p className="field-hint">
            ניתן לשנות בכל עת בהגדרות הקליניקה ← התאמה אישית.
          </p>
        </div>

        <div className="step-actions step-actions-between">
          <CustomClinikitButton
            type="button"
            variant="ghost"
            size="medium"
            onClick={handleBack}
          >
            <ArrowRight size={18} weight="bold" />
            חזרה
          </CustomClinikitButton>
          <CustomClinikitButton type="submit" variant="primary" size="large">
            המשך
            <ArrowLeft size={18} weight="bold" />
          </CustomClinikitButton>
        </div>
      </form>
    </div>
  );
}

export default PreferencesStep;
